/**
 * Merge eligibility for a set of selected invoices. The backend `/invoices/merge`
 * requires the selected invoices to share invoice type, customer, billing scope
 * and sale type, and forbids merging any already-paid/settled invoice. We mirror
 * that here so the UI can gate + explain the Merge action before any request.
 */
import type { InvoiceRow } from '@/api/invoices';

export interface MergeEligibility {
  ok: boolean;
  /** Human-readable reason the selection can't be merged (empty when `ok`). */
  reason: string;
}

/** Statuses whose invoices are settled and therefore cannot be merged. */
const LOCKED_STATUSES = new Set(['paid', 'done', 'refunded']);

/**
 * Validate a selection for merge. Fewer than two rows is "not enough" (not an
 * error the user needs to fix mid-selection). Otherwise all rows must agree on
 * type / customer / scope / sale type and none may be settled.
 */
export function evaluateMergeEligibility(rows: InvoiceRow[]): MergeEligibility {
  if (rows.length < 2) {
    return { ok: false, reason: 'Select at least two invoices to merge.' };
  }

  const locked = rows.find((r) => LOCKED_STATUSES.has(r.invoice_status));
  if (locked) {
    return {
      ok: false,
      reason: `Invoice ${locked.invoice_code} is ${locked.invoice_status} and cannot be merged.`,
    };
  }

  const [first, ...rest] = rows;
  const mismatch = rest.find(
    (r) =>
      r.invoice_type !== first.invoice_type ||
      r.customer?.customer_id !== first.customer?.customer_id ||
      r.invoice_billing_scope !== first.invoice_billing_scope ||
      r.invoice_sale_type !== first.invoice_sale_type,
  );
  if (mismatch) {
    return {
      ok: false,
      reason: 'Selected invoices must share the same type, customer, billing scope and sale type.',
    };
  }

  return { ok: true, reason: '' };
}
